<template>
  <div
    class="fixed inset-0 z-inpage-modal w-screen bg-black1 font-Spectral text-base backdrop-blur md:flex md:flex-col md:items-center md:justify-start md:bg-black-transparent-60 md:py-16 md:backdrop-blur-none"
  >
    <div ref="target" class="h-full w-screen max-w-[640px] overflow-hidden bg-white">
      <div class="relative flex h-full w-full flex-col bg-transparent">
        <div class="top-0 z-20 w-full font-medium leading-normal duration-300" :class="!isSP && isThemeTagImgVisible ? 'text-white' : 'text-stone-700'">
          <div class="flex items-center justify-between py-2 pl-4 pr-2">
            <p>Search</p>
            <div class="flex h-10 w-10 items-center justify-center p-2">
              <img v-if="isSP || !isThemeTagImgVisible" alt="シートを閉じるボタン" class="cursor-pointer" src="@/assets/icon/close/black.svg" @click.stop="props.onClose" />
              <img v-else alt="シートを閉じるボタン" class="h-[14px] w-[14px] cursor-pointer" src="@/assets/icon/close/white.svg" @click.stop="props.onClose" />
            </div>
          </div>
          <div class="overflow-x-auto">
            <!-- タブコンポーネント start -->
            <ul class="flex w-full min-w-max justify-center border-b border-stone-300 md:border-b-0">
              <li v-for="(tab, index) in menu" :key="tab.title" class="md:flex-1" data-testid="tab">
                <div class="flex flex-col pb-[8px] pt-3 md:py-3">
                  <button
                    class="relative z-10 -mb-[9px] flex items-center justify-center whitespace-nowrap border-b-[1px] px-2 py-1 text-center md:mb-0 md:w-full md:min-w-[9rem] md:px-1 md:py-2"
                    :class="[index === currentTabIndex ? 'border-gold1' : 'border-transparent md:border-stone-300', index === 0 ? 'pl-2 md:pl-1' : '', index === menu.length - 1 ? 'pr-2 md:pr-1' : '']"
                    :data-testid="'tab-' + tab.title"
                    @click="() => onClickTab(index)"
                  >
                    <img
                      v-if="tab.defaultIcon"
                      class="mr-1"
                      height="24"
                      width="24"
                      :alt="!isSP && isThemeTagImgVisible ? tab.whiteIcon.alt : index === currentTabIndex ? tab.defaultIcon?.alt : tab.disabledIcon?.alt"
                      :src="getAssetPath(!isSP && isThemeTagImgVisible ? tab.whiteIcon.src : index === currentTabIndex ? tab.defaultIcon?.src : tab.disabledIcon?.src)"
                    />
                    <span class="font-medium text-neutral-900" :class="[{ 'text-white': !isSP && isThemeTagImgVisible }, { 'text-stone-500': index !== currentTabIndex }]">
                      {{ tab.title }}
                    </span>
                  </button>
                </div>
              </li>
            </ul>
            <!-- タブコンポーネント end -->
          </div>
        </div>
        <!-- Destinations / Themes タブ用 -->
        <div v-show="currentTab.title !== 'Dates'" class="h-full w-full flex-1 overflow-auto px-4 md:px-10">
          <!-- タグ未取得時の UI（一度でも成功したら TagSelector を表示し続ける） -->
          <div v-if="!hasTagsEverSucceeded" class="flex flex-col items-center justify-center pb-10 pt-10 text-center">
            <template v-if="hasTagsError">
              <p class="text-lg font-normal leading-normal tracking-wide text-stone-700 md:text-xl md:leading-loose">Something went wrong.</p>
              <p class="my-4 px-2 text-base font-light leading-normal text-stone-500">Please try again.</p>
            </template>
            <template v-else>
              <p class="my-4 px-2 text-base font-light leading-normal text-stone-500">Loading...</p>
            </template>
            <button
              v-if="hasTagsError"
              class="w-full max-w-xs rounded border border-stone-300 px-4 py-3 text-base font-medium text-stone-700 transition-colors hover:bg-stone-50"
              data-testid="retry-tags-button"
              @click="onClickRetryTags"
            >
              Retry
            </button>
          </div>
          <!-- タグ取得成功時の通常表示 -->
          <div v-else :key="renderKey" class="w-full">
            <div v-show="currentTab.title === 'Destinations'">
              <TagSelector
                type="Destinations"
                :default-img-path="currentTab.defaultTagImgPath"
                :default-select="defaultDestinationTags"
                :on-click-tag-raw="onClickDestinationRaw"
                :tag-groups="selectableDestinationTagGroups"
                @selection-change="updateSelectedDestinationTags"
              />
            </div>
            <div v-show="currentTab.title === 'Themes'">
              <TagSelector
                type="Themes"
                :default-img-path="currentTab.defaultTagImgPath"
                :default-select="defaultThemeTags"
                :on-click-tag-raw="onClickThemeRaw"
                :tag-groups="selectableThemeTagGroups"
                :toggle-theme-tag-img-visibility="toggleTagImgVisibility"
                @selection-change="updateSelectedThemeTags"
              />
            </div>
            <div v-show="currentTab.title === 'Preferences'">
              <TagSelector
                type="Preferences"
                :default-img-path="currentTab.defaultTagImgPath"
                :default-select="defaultPreferenceTags"
                :on-click-tag-raw="onClickPreferenceRaw"
                :tag-groups="selectablePreferenceTagGroups"
                @selection-change="updateSelectedPreferenceTags"
              >
              </TagSelector>
            </div>
          </div>
        </div>
        <!-- Dates タブ用 -->
        <DateTimePickerWrapper
          v-show="currentTab.title === 'Dates'"
          :key="renderKey"
          class="flex-1"
          :default-dates="defaultDates"
          :default-time-of-day="defaultTimeOfDay"
          :on-dates-change="updateSelectedDates"
          :on-time-of-day-change="updateSelectedTimeOfDay"
          :on-time-range-change="updateSelectedTimeRange"
        />
        <div class="bottom-0 left-0 right-0 z-10 flex justify-center border-t px-4 py-2 duration-300" :class="!isSP && isThemeTagImgVisible ? 'border-white' : 'border-stone-300'">
          <template v-if="hasSearchError">
            <div class="flex w-full max-w-sm flex-col items-center gap-1 py-1 text-center">
              <p class="text-base font-normal leading-normal tracking-wide text-stone-700">Something went wrong.</p>
              <p class="text-sm font-light leading-normal text-stone-500">Please try again.</p>
              <button
                class="mt-1 w-full rounded border border-stone-300 px-4 py-3 text-base font-medium text-stone-700 transition-colors hover:bg-stone-50"
                data-testid="retry-search-button"
                @click="onClickRetrySearch"
              >
                Retry
              </button>
            </div>
          </template>
          <template v-else>
            <PrimaryButton class="h-12 w-full max-w-sm border-transparent" data-testid="search-submit-button" :on-click="onClickSearch">Show {{ totalCount }} Experiences</PrimaryButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { stringsFromQueryValue } from '@japan-culture-and-technology/calchez-utils/src/queryUtil';
import { onClickOutside } from '@vueuse/core';

import { excludeZeroResultTags, excludeZeroResultTagWebPaths } from '@/components/organisms/SearchExperiences/excludeZeroResultTags';
import { generateSearchPath } from '@/components/organisms/SearchExperiences/generateSearchPath';
import { searchMenu } from '@/components/organisms/SearchExperiences/searchMenu';
import TagSelector from '@/components/organisms/TagSelector/TagSelector.vue';
import { useAsyncWabunkaExperiencesSearch } from '@/composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useCurrentTabIndex } from '@/composables/ui/useCurrentTabIndex';
import { useIsSP } from '@/composables/ui/useIsSP';
import { useAsset } from '@/composables/utils/useAsset';
import { useNuxtRoute } from '@/composables/utils/useRoute';
import type { UseRuntimeConfig } from '@/injectionKeys';
import { useRuntimeConfigKey } from '@/injectionKeys';
import { queryToString } from '@/utils/query';
import { PrimaryButton } from '@/wabunka-src/common/components';
import DateTimePickerWrapper from '@/wabunka-src/common/components/stateful/DateTimePickerWrapper/DateTimePickerWrapper.vue';
import { isTimeOfDayValue, type TimeOfDayValue, type TimeRange } from '@/wabunka-src/common/components/stateful/TimeOfDayPicker/types';
import { withExperienceListSortQuery } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';
import { sendGAWabunkaSearchExperienceByDateEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByDate';
import { sendGAWabunkaSearchExperienceByDestinationEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByDestination';
import { sendGAWabunkaSearchExperienceByPreferenceEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByPreference';
import { sendGAWabunkaSearchExperienceByThemeEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByTheme';
import { sendGAWabunkaSearchExperienceByTimeOfDayEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByTimeOfDay';
import { sendGAWabunkaSelectDestinationInSearchModalEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectDestinationInSearchModal';
import { sendGAWabunkaSelectPreferenceInSearchModalEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectPreferenceInSearchModal';
import { sendGAWabunkaSelectThemeInSearchModalEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/selectThemeInSearchModal';
import { WABUNKA_ALL_AREA_TAG } from '@/wabunka-src/constants/wabunkaSearch';

interface Props {
  shouldShowModal: boolean;
  onClose: () => void; // モーダルを閉じる処理
}

const props = defineProps<Props>();
const route = useNuxtRoute();

const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
const runtimeConfig = useRuntimeConfig();

const isSP = useIsSP();

/**
 * パス、クエリパラメータ処理
 */
// パスの情報を取得
const destinationTagWebPathInRoutePath = computed<string>(() => {
  // /s/experiences/ページの場合
  if (route.name === 's-experiences-path' && route.params.path && route.params.path.length > 0 && route.params.path[0] !== WABUNKA_ALL_AREA_TAG) {
    return route.params.path[0];
  }
  // /destination/themeページの場合
  if (route.name === 'destination-theme' && route.params.destination) {
    return route.params.destination as string;
  }
  // /destination/ページの場合（Destinationページ）
  if (route.name === 'destination' && route.params.destination) {
    return route.params.destination as string;
  }
  return '';
});
const themeTagWebPathInRoutePath = computed<string>(() => {
  // /s/experiences/ページの場合
  if (route.name === 's-experiences-path' && route.params.path && route.params.path.length > 1) {
    return route.params.path[1];
  }
  // /destination/themeページの場合
  if (route.name === 'destination-theme' && route.params.theme) {
    return route.params.theme as string;
  }
  // /japan/themeページの場合
  if (route.name === 'japan-theme' && route.params.theme) {
    return route.params.theme as string;
  }
  return '';
});

const defaultDestinationTags = computed<string[]>(() => excludeZeroResultTagWebPaths(stringsFromQueryValue(route.query?.destinations ?? destinationTagWebPathInRoutePath.value)));
const defaultThemeTags = computed<string[]>(() => excludeZeroResultTagWebPaths(stringsFromQueryValue(route.query?.themes ?? themeTagWebPathInRoutePath.value)));
const defaultPreferenceTags = computed<string[]>(() => excludeZeroResultTagWebPaths(stringsFromQueryValue(route.query?.preferences ?? '')));

const defaultDates = computed<string[]>(() => stringsFromQueryValue(route.query?.experienceEventStartAtDates ?? ''));
const defaultTimeOfDay = computed<TimeOfDayValue[]>(() => {
  const timeOfDayQuery = route.query?.timeOfDay;
  if (!timeOfDayQuery) return [];
  const values = stringsFromQueryValue(timeOfDayQuery);
  return values.filter(isTimeOfDayValue);
});

const selectedDestinationTags = ref<string>(defaultDestinationTags.value.join(','));
const selectedThemeTags = ref<string>(defaultThemeTags.value.join(','));
const selectedPreferenceTags = ref<string>(defaultPreferenceTags.value.join(','));
const selectedDates = ref<string>(defaultDates.value.join(','));
const selectedTimeOfDay = ref<string>(defaultTimeOfDay.value.join(','));
const selectedTimeRangeFrom = ref<string>('');
const selectedTimeRangeTo = ref<string>('');

const { currentTabIndex, updateTabIndex } = useCurrentTabIndex('searchExperiencesModalTabIndex');
const menu = computed(() => searchMenu(runtimeConfig.public.STORAGE_BASE_URL as string));
const currentTab = computed(() => menu.value[currentTabIndex.value]);
// モーダルを再描画するためのキー
const renderKey = ref(0);

const { destinationTagGroups, themeTagGroups, preferenceTagGroups, hasError: hasTagsError, hasSucceeded: hasTagsSucceeded, refresh: refreshTags } = await useAsyncWabunkaTags({ throwOnError: false });
const selectableDestinationTagGroups = computed(() => excludeZeroResultTags(destinationTagGroups.value));
const selectableThemeTagGroups = computed(() => excludeZeroResultTags(themeTagGroups.value));
const selectablePreferenceTagGroups = computed(() => excludeZeroResultTags(preferenceTagGroups.value));
// ラッチ: 一度でも成功したかを記憶する。
// useFetch の refresh() はリクエスト中に data を null にし、status を 'pending' に変える。
// この中間状態で v-if が切り替わり、空の TagSelector が描画されてフリーズする問題への対処。
// 理想的には useAsyncWabunkaTags 側で「最後に成功したデータ」をキャッシュし、
// refresh 失敗時もデータを保持する設計にすれば、このラッチは不要になる。
// ただし useAsyncWabunkaTags は 6 箇所から呼ばれており影響範囲が大きいため、
// 今回はこのコンポーネントに閉じたワークアラウンドとしている。
const hasTagsEverSucceeded = ref(hasTagsSucceeded.value);
watch(
  () => hasTagsSucceeded.value,
  (v) => {
    if (v) hasTagsEverSucceeded.value = true;
  },
);
const {
  refresh: refreshExperiencesSearch,
  totalCount,
  hasError: hasSearchError,
} = await useAsyncWabunkaExperiencesSearch({
  experienceEventStartAtDates: selectedDates,
  destinations: selectedDestinationTags,
  themes: selectedThemeTags,
  preferences: selectedPreferenceTags,
  slugs: ref([]),
  experienceEventStartTimeRangeFrom: selectedTimeRangeFrom,
  experienceEventStartTimeRangeTo: selectedTimeRangeTo,
});

const { getAssetPath } = useAsset();

const updateSelectedDestinationTags = (tags: Map<string, string>) => {
  selectedDestinationTags.value = Array.from(tags.values()).join(',');
};

const updateSelectedThemeTags = (tags: Map<string, string>) => {
  selectedThemeTags.value = Array.from(tags.values()).join(',');
};

const updateSelectedPreferenceTags = (tags: Map<string, string>) => {
  selectedPreferenceTags.value = Array.from(tags.values()).join(',');
};

const updateSelectedDates = (dates: Map<string, string>) => {
  selectedDates.value = Array.from(dates.values()).join(',');
};

const updateSelectedTimeOfDay = (timeOfDay: Set<TimeOfDayValue>) => {
  selectedTimeOfDay.value = Array.from(timeOfDay.values()).join(',');
};

const updateSelectedTimeRange = (timeRange: TimeRange | null) => {
  selectedTimeRangeFrom.value = timeRange?.from ?? '';
  selectedTimeRangeTo.value = timeRange?.to ?? '';
};

const onClickTab = (tabNumber: number) => {
  updateTabIndex(tabNumber);
};

const onClickDestinationRaw = (tagName: string) => {
  // GAイベント送信
  sendGAWabunkaSelectDestinationInSearchModalEvent(tagName);
};

const onClickThemeRaw = (tagName: string) => {
  // GAイベント送信
  sendGAWabunkaSelectThemeInSearchModalEvent(tagName);
};

const onClickPreferenceRaw = (tagName: string) => {
  // GAイベント送信
  sendGAWabunkaSelectPreferenceInSearchModalEvent(tagName);
};

const onClickRetryTags = () => {
  refreshTags();
};

const onClickRetrySearch = () => {
  refreshExperiencesSearch();
};

const onClickSearch = () => {
  props.onClose();

  // GAイベント送信
  sendGAWabunkaSearchExperienceByThemeEvent(selectedThemeTags.value);
  sendGAWabunkaSearchExperienceByDestinationEvent(selectedDestinationTags.value);
  sendGAWabunkaSearchExperienceByPreferenceEvent(selectedPreferenceTags.value);
  sendGAWabunkaSearchExperienceByDateEvent(selectedDates.value);
  sendGAWabunkaSearchExperienceByTimeOfDayEvent(selectedTimeOfDay.value);

  // 遷移先は検索結果ページともエリア / テーマページとも成り得るが、いずれもソートを解釈する
  const searchPath = generateSearchPath(selectedDestinationTags.value, selectedThemeTags.value, selectedDates.value, selectedTimeOfDay.value, selectedPreferenceTags.value);

  Promise.resolve(navigateTo({ ...searchPath, query: withExperienceListSortQuery(searchPath.query, queryToString(route.query?.sort)) })).then(() => {
    const currentPath = route.path as string;
    if (currentPath.startsWith('/s/experiences/')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
};

/**
 * モーダルの制御
 */
// click-outsideの対象の要素
const target = ref<HTMLElement | null>(null);

const setModalHeight = () => {
  if (!window.document) return;
  const height = window.innerHeight;
  document.documentElement.style.setProperty('--modalHeight', `${height}px`);
};

onMounted(() => {
  setModalHeight();
  window.addEventListener('resize', setModalHeight);
});

const lockBodyScroll = () => {
  if (!window.document) return;
  const body = document.getElementsByTagName('body')[0];
  body.style.overflow = 'hidden';
  body.style.touchAction = 'none';
};

const resetBodyStyle = () => {
  if (!window.document) return;
  const body = document.getElementsByTagName('body')[0];
  body.style.overflow = '';
  body.style.touchAction = '';
};

watch(
  () => props.shouldShowModal,
  (val) => {
    selectedDestinationTags.value = defaultDestinationTags.value.join(',');
    selectedDates.value = defaultDates.value.join(',');
    selectedThemeTags.value = defaultThemeTags.value.join(',');
    selectedPreferenceTags.value = defaultPreferenceTags.value.join(',');
    selectedTimeOfDay.value = defaultTimeOfDay.value.join(',');
    // timeRangeはdefaultTimeOfDayから計算する必要があるが、初期値は空で良い（モーダル開いた時に再計算される）
    selectedTimeRangeFrom.value = '';
    selectedTimeRangeTo.value = '';
    if (val) {
      // 成功済みの場合は refresh しない。refresh() 失敗時に data が消えるため。
      // useAsyncWabunkaTags 側でキャッシュを持てば、常に refresh できるようになる。
      if (!hasTagsEverSucceeded.value) {
        refreshTags();
      }
      refreshExperiencesSearch();
      // 再度モーダルを開いたときに、選択状態が初期化されるよう再レンダリングする ※こちらの処理がないと、選択状態が保持されたままになる
      renderKey.value = Number(!renderKey.value);
      lockBodyScroll(); // v-scroll-lock を使用すると、モーダル内のコンテンツがスマホでスクロールできなかったので、自前で body のスクロールを制御している
    } else {
      resetBodyStyle();
    }
  },
);

watch(selectedDates, () => {
  if (props.shouldShowModal) {
    refreshExperiencesSearch();
  }
});

watch([selectedTimeRangeFrom, selectedTimeRangeTo], () => {
  if (props.shouldShowModal) {
    refreshExperiencesSearch();
  }
});

onClickOutside(target, props.onClose);
onUnmounted(resetBodyStyle);

// Theme TagのHover時（PCのみ）に背景画像を表示する際に、モーダル内のテキスト色等を変更するため、
// TabSelector Componentに状態を注入する
const isThemeTagImgVisible = ref<boolean>(false);
const toggleTagImgVisibility = (value: boolean) => {
  isThemeTagImgVisible.value = value;
};
</script>
